import * as Yup from "../util/yup.js";

import { ErrorMessage, Field, Form, Formik } from "formik";
import { __, _x } from "@wordpress/i18n";

import FormField from "./FormField.jsx";
import { StoreAutocomplete } from "./StoreAutocomplete.jsx";

const profileOptions = [
  {
    value: "customer",
    label: _x("Déjà client", "Frontend forms", "manitoba-leads"),
  },
  {
    value: "prospect",
    label: _x("Pas encore client", "Frontend forms", "manitoba-leads"),
  },
];
// Les demandes des visiteurs déjà clients restent dans le code : elles ne sont pas
// transmises à Lead Factory et n'ont donc pas d'identifiant à porter. Celles des
// prospects viennent du réglage du back-office, via la prop « needs ».
const customerRequestOptions = [
  {
    value: _x("Support technique", "Frontend forms", "manitoba-leads"),
    label: _x("Support technique", "Frontend forms", "manitoba-leads"),
  },
  {
    value: _x("Service commercial", "Frontend forms", "manitoba-leads"),
    label: _x("Service commercial", "Frontend forms", "manitoba-leads"),
  },
  {
    value: _x("Demande administrative", "Frontend forms", "manitoba-leads"),
    label: _x("Demande administrative", "Frontend forms", "manitoba-leads"),
  },
];

/**
 * Les options du champ « Votre demande concerne », selon le profil choisi.
 * Pour un prospect, la valeur est l'identifiant XEFI et le libellé celui saisi
 * dans les réglages ; pour un client, les deux restent le libellé traduit.
 */
function requestOptionsFor(profile, needs) {
  // Yup 1.x passe au builder de .when() un tableau des valeurs observées, là où
  // le formulaire fournit une chaîne. On accepte les deux formes.
  if (Array.isArray(profile)) {
    profile = profile[0];
  }

  if (profile === "prospect") {
    return needs.map((need) => ({ value: String(need.id), label: need.label }));
  }

  return customerRequestOptions;
}

const contactSchema = (hasAcceptTerms = false, needs = []) =>
  Yup.object().shape({
    profile: Yup.string()
      .required()
      .oneOf(profileOptions.map((option) => option.value)),
    request: Yup.string()
      .required()
      .when("profile", function (profile, schema) {
        // La liste des prospects venant des réglages, le schéma doit être
        // construit avec elle : sinon oneOf refuse tout identifiant saisi.
        const options = requestOptionsFor(profile, needs);
        return schema.oneOf(options.map((option) => option.value));
      }),
    companyName: Yup.string().required().trim(),
    postalCode: Yup.string().required().trim(),
    // .matches(
    //   /^\d{5}$/,
    //   _x(
    //     "Le code postal doit contenir 5 chiffres",
    //     "Frontend validation",
    //     "manitoba-leads"
    //   ),
    //   true
    // )
    store: Yup.mixed()
      .nullable()
      .when("profile", {
        is: "customer",
        then: (schema) => schema.test(
          "required",
          __("Ce champ est obligatoire", "manitoba-leads"),
          (value) => !!(value?.name)
        ),
        otherwise: (schema) => schema.notRequired(),
      }),
    firstName: Yup.string().required().trim(),
    lastName: Yup.string().required().trim(),
    phone: Yup.string().required().trim(),
    // .test(
    //   "has-ten-digits",
    //   _x(
    //     "Le numéro de téléphone doit contenir 10 chiffres",
    //     "Frontend validation",
    //     "manitoba-leads"
    //   ),
    //   (phone) =>
    //     !phone || (phone || "").replace(/\D/gim, "").match(/^\d{10}$/)
    // )
    // .matches(
    //   /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/g,
    //   _x(
    //     "Ce champ doit être un numéro de téléphone",
    //     "Frontend validation",
    //     "manitoba-leads"
    //   )
    // )
    email: Yup.string().required().trim().email(),
    message: Yup.string().trim(),
    ...(hasAcceptTerms && {
      accept: Yup.boolean().isTrue(
        _x(
          "Vous devez valider les conditions",
          "Frontend validation",
          "manitoba-leads"
        )
      ),
    }),
  });

export default function ContactForm({
  onSubmit,
  storeId,
  kind,
  message,
  acceptTerms,
  needs = [],
}) {
  return (
    <Formik
      initialValues={{
        store: storeId ? { id: storeId } : null,
        source: "quickaccess",
        kind,
        profile: "prospect",
        pageUrl: window.location.href,
        message,
      }}
      validationSchema={contactSchema(!!acceptTerms, needs)}
      validateOnChange={true}
      validateOnBlur={true}
      onSubmit={onSubmit}
    >
      {({ values, setFieldValue }) => (
        <Form className={"leads-form"}>
          <div className="leads-form-content">
            <FormField
              type="radio"
              name="profile"
              required
              options={profileOptions}
              label={_x("Je suis", "Frontend forms", "manitoba-leads")}
            />

            {requestOptionsFor(values.profile, needs).length > 0 && (
              <FormField
                as="select"
                name="request"
                required
                label={_x(
                  "Votre demande concerne",
                  "Frontend forms",
                  "manitoba-leads"
                )}
                options={requestOptionsFor(values.profile, needs)}
                placeholder={_x(
                  "Sélectionnez une option...",
                  "Frontend forms",
                  "manitoba-leads"
                )}
              />
            )}

            <FormField
              type="text"
              name="companyName"
              required
              label={_x("Société", "Frontend forms", "manitoba-leads")}
              placeholder={_x(
                "Ex : Ma Société",
                "Frontend forms",
                "manitoba-leads"
              )}
            />

            <FormField
              type="text"
              name="postalCode"
              required
              label={_x("Code postal", "Frontend forms", "manitoba-leads")}
              placeholder={_x("Ex : 92200", "Frontend forms", "manitoba-leads")}
            />

            {values.profile === "customer" && (
              <FormField
                type="select"
                name="store"
                required
                label={_x("Votre agence", "Frontend forms", "manitoba-leads")}
                placeholder={_x(
                  "Sélectionnez votre agence...",
                  "Frontend forms",
                  "manitoba-leads"
                )}
              >
                <Field
                  name="store"
                  as={StoreAutocomplete}
                  onChange={(store) => setFieldValue("store", store)}
                  storeSearchRequest={
                    values.postalCode ? values.postalCode.substring(0, 2) : ""
                  }
                />
              </FormField>
            )}

            <FormField
              type="text"
              name="firstName"
              required
              label={_x("Prénom", "Frontend forms", "manitoba-leads")}
              placeholder={_x(
                "Ex : Dominique",
                "Frontend forms",
                "manitoba-leads"
              )}
            />

            <FormField
              type="text"
              name="lastName"
              required
              label={_x("Nom", "Frontend forms", "manitoba-leads")}
              placeholder={_x(
                "Ex : Dupond",
                "Frontend forms",
                "manitoba-leads"
              )}
            />

            <FormField
              type="text"
              name="phone"
              required
              label={_x("Nᵒ de téléphone", "Frontend forms", "manitoba-leads")}
              placeholder={_x(
                "Ex : 06 00 00 00 00",
                "Frontend forms",
                "manitoba-leads"
              )}
            />

            <FormField
              type="email"
              name="email"
              required
              label={_x("Email", "Frontend forms", "manitoba-leads")}
              placeholder={_x(
                "Ex : votreadresse@email.com",
                "Frontend forms",
                "manitoba-leads"
              )}
            />

            <FormField
              as="textarea"
              name="message"
              label={_x("Mon message", "Frontend forms", "manitoba-leads")}
              rows={5}
              placeholder={_x(
                "Décrivez ici svp votre besoin. Merci.",
                "Frontend forms",
                "manitoba-leads"
              )}
            />

            {!!acceptTerms && (
              <div className="contact-form-accept">
                <label className="contact-form-accept-label">
                  <Field
                    className="contact-form-accept-input"
                    type="checkbox"
                    name="accept"
                    required
                  />
                  <div
                    className="contact-form-accept-text"
                    dangerouslySetInnerHTML={{ __html: acceptTerms }}
                  ></div>
                </label>
                <ErrorMessage name="accept">
                  {(message) => (
                    <div className={`form-field-error`}>
                      <p className={`form-field-error-message`}>{message}</p>
                    </div>
                  )}
                </ErrorMessage>
              </div>
            )}
          </div>

          <div className="leads-form-footer">
            <button
              type="submit"
              className="leads-form-button leads-form-button--submit"
            >
              {kind === "contact"
                ? _x("Envoyer ma demande", "Frontend forms", "manitoba-leads")
                : _x(
                    "Me rappeler rapidement",
                    "Frontend forms",
                    "manitoba-leads"
                  )}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
